import { spawnSync } from 'node:child_process';
import { homedir } from 'node:os';
import { join } from 'node:path';
import { fileExists, resolveExecutable } from './external_editor';
import { MIN_CREDENTIAL_HELPER_VERSION } from './glab_credential_helper';

export interface GlabLocation {
  path: string;
  version: string | null;
  reachableViaSpawn: boolean;
  isSnap: boolean;
}

export interface GlabDetection {
  /** Whether bare `spawnSync('glab', …)` works — the exact call the credential helper makes. */
  spawnReachable: boolean;
  spawnVersion: string | null;
  /** Reachable, but older than the credential-helper minimum so it can't serve credentials. */
  spawnTooOld: boolean;
  /** De-duped on-disk hits (snap, homebrew, mise/asdf, winget, …). */
  locations: GlabLocation[];
  /** A snap install exists but bare `glab` can't be spawned — the confinement case. */
  snapOnly: boolean;
}

export interface DetectGlabOptions {
  env?: NodeJS.ProcessEnv;
  platform?: NodeJS.Platform;
  home?: string;
}

const SPAWN_TIMEOUT_MS = 5000;
const INSTALL_DOCS_URL = 'https://gitlab.com/gitlab-org/cli/-/blob/main/README.md#installation';

const SNAP_PREFIX = '/snap/';

function parseGlabVersion(stdout: string | undefined): string | null {
  if (!stdout) return null;
  const match = stdout.match(/\bv?(\d+\.\d+\.\d+\S*)/);
  return match ? match[1] : null;
}

function versionParts(version: string): [number, number, number] {
  const [major = '0', minor = '0', patch = '0'] = version.split('.');
  return [parseInt(major, 10) || 0, parseInt(minor, 10) || 0, parseInt(patch, 10) || 0];
}

function isBelowVersion(version: string, minimum: string): boolean {
  const actual = versionParts(version);
  const min = versionParts(minimum);
  for (let i = 0; i < 3; i += 1) {
    if (actual[i] !== min[i]) return actual[i] < min[i];
  }
  return false;
}

function probeGlabVersion(command: string): { ran: boolean; version: string | null } {
  const result = spawnSync(command, ['--version'], {
    encoding: 'utf-8',
    timeout: SPAWN_TIMEOUT_MS,
  });
  if (result.error || result.status !== 0) {
    return { ran: false, version: null };
  }
  return { ran: true, version: parseGlabVersion(result.stdout) };
}

/**
 * Under WSL2, the current Windows user's winget `Links` dir is mounted onto
 * `$PATH` via interop. Scoping to PATH keeps us on the current user's shim (no
 * other profile's shim is reachable anyway) without enumerating `/mnt/c/Users`.
 */
function wslWingetLinks(env: NodeJS.ProcessEnv): string[] {
  return (env.PATH ?? '')
    .split(':')
    .filter((dir) => /\/WinGet\/Links\/?$/i.test(dir))
    .map((dir) => join(dir, 'glab.exe'));
}

function candidatePaths(platform: NodeJS.Platform, env: NodeJS.ProcessEnv, home: string): string[] {
  const miseAsdf = [
    join(home, '.local', 'share', 'mise', 'shims', 'glab'),
    join(home, '.asdf', 'shims', 'glab'),
  ];

  if (platform === 'darwin') {
    return ['/opt/homebrew/bin/glab', '/usr/local/bin/glab', ...miseAsdf];
  }

  if (platform === 'win32') {
    return [
      'C:\\Program Files\\GitLab CLI\\bin\\glab.exe',
      join(home, 'scoop', 'shims', 'glab.exe'),
      join(home, 'AppData', 'Local', 'Microsoft', 'WinGet', 'Links', 'glab.exe'),
    ];
  }

  // linux (incl. WSL2)
  const linux = [
    '/snap/bin/glab',
    '/usr/local/bin/glab',
    '/usr/bin/glab',
    join(home, '.local', 'bin', 'glab'),
    ...miseAsdf,
  ];

  const isWsl = Boolean(env.WSL_DISTRO_NAME || env.WSL_INTEROP);
  if (isWsl) {
    linux.push('/mnt/c/Program Files/GitLab CLI/bin/glab.exe', ...wslWingetLinks(env));
  }

  return linux;
}

export function detectGlab(opts: DetectGlabOptions = {}): GlabDetection {
  const env = opts.env ?? process.env;
  const platform = opts.platform ?? process.platform;
  const home = opts.home ?? homedir();

  const bareCommand =
    platform === 'win32' ? (resolveExecutable('glab.exe', env) ?? 'glab.exe') : 'glab';
  const bare = probeGlabVersion(bareCommand);

  const uniquePaths = [...new Set(candidatePaths(platform, env, home))].filter(fileExists);
  const locations: GlabLocation[] = uniquePaths.map((path) => {
    const probe = probeGlabVersion(path);
    return {
      path,
      version: probe.version,
      reachableViaSpawn: probe.ran,
      isSnap: path.startsWith(SNAP_PREFIX),
    };
  });

  const snapOnly = locations.length > 0 && locations.every((l) => l.isSnap) && !bare.ran;
  const spawnTooOld =
    bare.ran &&
    bare.version !== null &&
    isBelowVersion(bare.version, MIN_CREDENTIAL_HELPER_VERSION);

  return {
    spawnReachable: bare.ran,
    spawnVersion: bare.version,
    spawnTooOld,
    locations,
    snapOnly,
  };
}

let cachedDetection: GlabDetection | undefined;

/**
 * Process-lifetime memoization of {@link detectGlab}. glab's install state is
 * static within a single CLI run, and detection re-runs on every credential
 * miss (and in doctor), so we probe the filesystem/PATH only once. Tests call
 * {@link detectGlab} directly to stay deterministic.
 */
export function detectGlabOnce(): GlabDetection {
  cachedDetection ??= detectGlab();
  return cachedDetection;
}

export function glabRemediationHint(detection: GlabDetection): string | null {
  if (detection.spawnReachable) {
    if (detection.spawnTooOld) {
      return `glab ${detection.spawnVersion} is too old for the credential helper — the Duo CLI needs glab ${MIN_CREDENTIAL_HELPER_VERSION} or newer. Upgrade glab (a non-snap install is recommended). See ${INSTALL_DOCS_URL}`;
    }
    return null;
  }

  if (detection.snapOnly) {
    return 'glab is installed via snap (/snap/bin/glab) but the Duo CLI cannot run it. In confined environments such as WSL2 this often cannot be fixed by PATH changes — a non-snap install (mise, deb, or a prebuilt binary) is the reliable fix. Otherwise, ensure /snap/bin is on the PATH of the process that launches the Duo CLI.';
  }

  if (detection.locations.length > 0) {
    return `glab is installed but not on the Duo CLI subprocess PATH. Ensure the directory containing glab is on PATH for the process that launches the Duo CLI. See ${INSTALL_DOCS_URL}`;
  }

  return null;
}
